// Copyright ©️ 2026 eVolve MEP, LLC

import type { CustomFilterProps } from 'ag-grid-react';

import type { CustomStatusEntityName, EntityState, StateIdFor } from 'components/Mantine/StatusEditor/types';
import type { StateColorMap } from 'components/Mantine/StatusEditor/useCustomStatuses';
import { CustomValueSetFilterComponentWrapped, type SetFilterModal } from 'helpers/ag-grid/CustomSetFilterComponent';
import { isKeyOf } from 'helpers/isKeyOf';
import { groupBy, strongEntries } from 'helpers/strongEntries';

export type EntityStateAgGridContext<TEntity extends CustomStatusEntityName> = {
  entityStates: readonly EntityState<TEntity>[];
  stateLabelMap: StateColorMap<TEntity>;
};

type EntityFilterOption<TEntity extends CustomStatusEntityName> = { label: string; ids: StateIdFor<TEntity>[] };
type EntityFilterValue<TEntity extends CustomStatusEntityName> = StateIdFor<TEntity>[];

/**
 * Used to generate a selectable set of Statuses (States) for filtering in AG Grid.
 *
 * For a given set of `entityStates` from context (each with an ID and Name),
 * finds the UI labels for each type and groups by label.
 * This way, if you select a label that covers multiple status type IDs,
 * all of those IDs will be selected, instead of just one.
 *
 * For example, `taskStatusTypeNames` "Paused" and "Started"
 * both have the label "In Progress" from `statusToColorMap`
 * so we want to group them together.
 */
export const EntitySetFilterComponent = <TData, TEntity extends CustomStatusEntityName>(filterKey: string) =>
  function (
    filterProps: CustomFilterProps<
      TData,
      EntityStateAgGridContext<TEntity>,
      SetFilterModal<EntityFilterValue<TEntity>>
    >,
  ) {
    const { entityStates, stateLabelMap } = filterProps.context;
    const values: EntityFilterOption<TEntity>[] = strongEntries(
      groupBy(
        entityStates
          // Quick filter *just to make sure* our states all have labels
          .filter((t) => isKeyOf(t.name, stateLabelMap))
          .map(({ name, stateId }) => ({
            label: stateLabelMap[name].label,
            id: stateId,
          })),
        (t) => t.label,
      ),
    ).map(([label, groupedStatusTypes]) => ({
      label,
      ids: groupedStatusTypes.map((a) => a.id),
    }));

    return (
      <CustomValueSetFilterComponentWrapped
        {...filterProps}
        values={values}
        valueFormatter={({ value }) => value?.label ?? ''}
        keyCreator={({ value }) => value?.ids ?? []}
        suppressSorting
        buttons={['apply', 'reset']}
        filterKey={filterKey}
      />
    );
  };
